import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import ImageCarousel from "components/ImageCarousel";
import InnerLoading from "components/InnerLoading";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";
import {
  resetPremiumInventory,
  setActualHeldDetail,
  setOriginalHeldDetail,
  setSelectedPartySize,
} from "features/slices/reservationFlowSlice";
import { SeatDetails } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePreferredSections from "hooks/usePreferredSections";
import { ScheduleStackParamList } from "navigation/types";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  fetchBestBlocksRequest,
  holdTicketsRequest,
} from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import SeatQuantitySelector from "screens/SeatSelection/components/SeatQuantitySelector";
import SectionList from "screens/SeatSelection/components/SectionList";
import VenueSeatMap from "screens/SeatSelection/components/VenueSeatMap";
import { getPremiumImageUrls } from "screens/SeatSelection/helpers";
import { formatCurrency } from "utils/appUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
interface State {
  step: 1 | 2;
  totalSeats: number;
  viewMoreToggle: boolean;
}

/**
 *
 *  1. class type === 'SEATS': Book Individual Seats (choose section/row/seat)
 *  2. class type === 'GA':    Book Entire Suite/Table/Space
 *    a. allowPartials = TRUE, then allow user to select "additional seats"
 *    b. allowPartials = FALSE, then user must book entire suite and all seats in the CODE in full.
 */
``;
const PremiumCodeDetailsScreen = () => {
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "PremiumCodeDetails">
    >();
  // react-queries
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const {
    mutate: fetchBestBlocks,
    data: bestBlocks,
    isLoading: fetchingBlocks,
  } = useMutation(fetchBestBlocksRequest);
  const { mutateAsync: holdTickets, isLoading: holdingTickets } =
    useMutation(holdTicketsRequest);
  // redux
  const dispatch = useAppDispatch();
  const {
    selectedEvent,
    selectedSubscription,
    premiumInventory,
    selectedPremiumCode,
  } = useAppSelector((state) => state.reservationFlow);
  const { tenantSubdomain, tenantType, tenantThemeColor } = useAppSelector(
    (state) => state.tenant,
  );

  // local state
  const {
    preferredSections,
    setPreferredSections,
    handleAddPreferredSection,
    handleRemovePreferredSection,
  } = usePreferredSections();
  const [state, setState] = useState<State>({
    step: 1,
    totalSeats: 0,
    viewMoreToggle: false,
  });
  const [listHeight, setListHeight] = useState(0);

  const slides = getPremiumImageUrls(
    premiumInventory!.premiumClass,
    selectedEvent!,
  );

  const {
    name: premiumClassName,
    longDescription,
    shortDescription,
    type: premiumClassType,
    allowPartials,
  } = premiumInventory!.premiumClass;
  const { name, sections } = selectedPremiumCode!;

  const isTicketProviderCaptured = selectedEvent?.ticketProvider === "CAPTURED";
  const selectingBestBlock = isTicketProviderCaptured || state.step === 2;

  // dynamically setting SectionList's height
  const onLayout = (event: any) => {
    const { height } = event.nativeEvent.layout;
    setListHeight(height);
  };

  const handleBestBlockPress = async (tickets: SeatDetails[]) => {
    dispatch(
      setOriginalHeldDetail({
        section: tickets[0].section,
        row: tickets[0].row,
      }),
    );
    // hold tickets
    const heldTickets = await holdTickets({
      planPackageId: selectedSubscription!.planPackage.id,
      eventId: selectedEvent!.eventId,
      partySize: state.totalSeats,
      premiumOnly: true,
      selections: tickets,
    });
    dispatch(setActualHeldDetail(heldTickets));
    // navigate to reserve page
    navigation.navigate("Reserve");
  };

  const handleBook = async () => {
    dispatch(
      setOriginalHeldDetail({
        section: bestBlocks![0].section,
        row: bestBlocks![0].row,
      }),
    );
    // hold tickets
    const heldTickets = await holdTickets({
      planPackageId: selectedSubscription!.planPackage.id,
      eventId: selectedEvent!.eventId,
      partySize: state.totalSeats,
      premiumOnly: true,
      selections: bestBlocks![0].tickets
        .slice(0, state.totalSeats)
        .map((block) => ({
          section: block.section,
          row: block.row,
          seat: block.seat,
        })),
    });
    dispatch(setActualHeldDetail(heldTickets));
    // navigate to reserve page
    navigation.navigate("Reserve");
  };

  const handleFetchBestBlocks = () => {
    fetchBestBlocks({
      eventId: selectedEvent?.eventId,
      planPackageId: selectedSubscription?.planPackage.id,
      sections: sections.map((section) => section.section),
      premiumOnly: true,
      partySize: premiumInventory!.minPartySize,
    });
  };

  const getMaxSeatQuantity = useMemo(() => {
    const { maxPartySize } = selectedPremiumCode!;
    if (premiumClassType === "SEATS") {
      return Math.min(
        maxPartySize,
        getMaxSeatsAllowedPerReservation(tenantConfig),
      );
    } else {
      return maxPartySize;
    }
  }, [selectedPremiumCode, premiumClassType]);

  const calculateTotal = useMemo(() => {
    if (!premiumInventory || !selectedPremiumCode) return 0;
    const {
      priceType,
      premiumClass: { allowPartials },
    } = premiumInventory;
    const { minPlanTicketCharge, maxPartySize, minPlanTicketFees } =
      selectedPremiumCode;
    const finalPartySize = allowPartials ? state.totalSeats : maxPartySize;

    return formatCurrency(
      (Number(minPlanTicketCharge) + Number(minPlanTicketFees)) *
        finalPartySize,
      {
        priceType,
      },
    );
  }, [premiumInventory, selectedPremiumCode, state.totalSeats]);

  useEffect(() => {
    if (!selectedPremiumCode) return;
    dispatch(setSelectedPartySize(selectedPremiumCode.minPartySize));
  }, [selectedPremiumCode]);

  // Do not show estimated total for NIL events
  // https://github.com/FanRally/fanrally-core/issues/1485
  const shouldShowTotal = !["NIL", "RSVP"].includes(
    selectedEvent?.eventType || "",
  );

  useEffect(() => {
    if (!selectedPremiumCode) return;
    setState((state) => ({
      ...state,
      totalSeats: Math.max(1, selectedPremiumCode.minPartySize),
    }));
  }, [selectedPremiumCode]);

  useEffect(() => {
    handleFetchBestBlocks();
  }, []);

  return (
    <MainContainer
      goBackAction={() => {
        navigation.goBack();
        // When navigating back, reset the premium inventory, so the Reserve page doesn't show the cached premium inventory data,
        // when the user selects a premium code and then goes back and chooses a regular game.
        dispatch(resetPremiumInventory());
      }}
    >
      <View style={{ padding: 8, flex: 1, height: "100%" }}>
        <View>
          <View>
            {premiumClassType === "GA" ? (
              <ImageCarousel sources={slides}></ImageCarousel>
            ) : (
              <View style={{ height: 200 }}>
                <VenueSeatMap
                  preferredSections={preferredSections}
                  onSectionAdded={handleAddPreferredSection}
                  onSectionRemoved={handleRemovePreferredSection}
                  mapId="premium-code"
                  tenantType={tenantType!}
                  tenantSubdomain={tenantSubdomain!}
                  eventType={selectedEvent!.eventType}
                  venueName={selectedEvent!.venueName}
                  theme={tenantThemeColor}
                  selectedPartySize={state.totalSeats}
                  blocks={bestBlocks || []}
                  usingStaticImage={false}
                  seatGroups={
                    selectedSubscription!.planPackage.primaryComponent
                      .seatGroups
                  }
                  includePremium={true}
                ></VenueSeatMap>
              </View>
            )}
          </View>
          <Spacer />
          {/* For EXPERIENCE and NIL events, just show the premium class name */}
          <Text category="h4">
            {premiumClassName}
            {["EXPERIENCE", "NIL", "RSVP"].includes(selectedEvent!.eventType)
              ? ""
              : ` - ${name}`}
          </Text>

          <Text category="c1">{shortDescription}</Text>

          <Spacer margin={6} />

          {state.viewMoreToggle && <Text category="c1">{longDescription}</Text>}
          {!state.viewMoreToggle && (
            <Pressable
              onPress={() => setState({ ...state, viewMoreToggle: true })}
            >
              <Text category="c1" status="info">
                See More
              </Text>
            </Pressable>
          )}
        </View>
        {/* 
            only allow to choose additional setas if:
              premium class type is SEATS 
              OR
              allowPartials is true 
        */}
        {(premiumClassType === "SEATS" || allowPartials) && (
          <View style={{ marginTop: 5 }}>
            <Spacer>
              <SeatQuantitySelector
                variant="ticker"
                size={getMaxSeatQuantity}
                selectedPartySize={state.totalSeats}
                onChange={(newSeats) => {
                  setState({
                    ...state,
                    totalSeats: newSeats,
                  });
                  dispatch(setSelectedPartySize(newSeats));
                }}
                minPartySize={selectedPremiumCode?.minPartySize}
              />
            </Spacer>
          </View>
        )}
        {premiumClassType === "GA" && (
          <>
            {shouldShowTotal && (
              <>
                <Text category="s2">Estimated total: {calculateTotal}</Text>
                <Text category="c1" style={{ marginTop: 8 }}>
                  Additional Cost will be shown on checkout page
                </Text>
              </>
            )}

            <Button
              onPress={handleBook}
              loading={holdingTickets || fetchingBlocks}
              style={{ marginTop: 12 }}
              disabled={!bestBlocks || bestBlocks.length === 0}
            >
              {holdingTickets || fetchingBlocks ? "Loading..." : "Book"}
            </Button>
          </>
        )}
        {premiumClassType === "SEATS" && (
          <View style={{ flex: 1, flexGrow: 1 }} onLayout={onLayout}>
            {fetchingBlocks || holdingTickets ? (
              <InnerLoading />
            ) : (
              <SectionList
                selectingBestBlock
                height={listHeight}
                handleSectionPress={() => {}}
                handleBestBlockPress={handleBestBlockPress}
                onBackBtnPress={() => {}}
                shouldShowGuestPrice={false}
                ticketProvider={selectedEvent?.ticketProvider || "CAPTURED"}
                seatSelectionStep={state.step}
                sections={sections}
                bestBlocks={bestBlocks}
                allBlocks={bestBlocks || []}
              />
            )}
          </View>
        )}
      </View>
    </MainContainer>
  );
};

export default PremiumCodeDetailsScreen;
